import React from 'react'
import {
  Button,
  Card,
  ConfirmDialog,
  Div,
  Flex,
  Icon as QDSIcon,
  InfoBlock,
  Input,
  PhoneInput,
  SelectableCard,
  Separator,
  Spacer,
  Span,
  Text,
  UserCard,
} from '@sendoutcards/quantum-design-ui'
import { Icon } from 'src/chrome'

import {
  AccountFragment,
  AccountInput,
  getMarketingContent,
  getPhoneNumberCountries,
  getSponsor,
  getSponsorBySlug,
  getSponsors,
  PhoneNumberCountryFragment,
  SponsorFragment,
} from 'src/graphql'

import FindSponsor from 'src/user/components/FindSponsor/FindSponsor'

import {
  useActions,
  useDebounce,
  useEffect,
  useMutations,
  useQueries,
  useQueryParams,
  useRedirectUriBuilder,
  useSelector,
  useState,
  useVertical,
} from 'src/hooks'
import { LoginJoinForgot } from 'src/user/components/JoinModal/JoinModal'
import { formatLocation } from 'src/helpers'
import usePersistedUserData from 'src/hooks/usePersistedUserData'
import { getFormattedNumber } from 'src/onboarding/components/PhoneNumberVerification'
import {
  createPhoneNumberInput,
  getValidatedNumber,
} from 'src/helpers/createPhoneNumberInput'
import { AffiliateFormInfo } from 'src/marketing/plans/components/AffiliateForm/AffiliateForm'
import AffiliateAddress from './AffiliateJoin/AffiliateAddress'
import PaymentMethod from './AffiliateJoin/PaymentMethod'
import { Result } from 'src/utils/Result'
import CreditCardLogo, {
  creditCardBrand,
} from 'src/payments/components/CardLogo/CreditCardLogo'

const sponsorSelectionCopy = {
  title: 'Who showed you this product?',
  message: (clientName: string) =>
    `We celebrate and give back to the people who invite others to experience ${clientName}. Let us know who showed you our products and we will make sure they are given the credit they deserve!`,
}

const usernameTakenCopy = {
  title: 'This Username is Taken',
  message:
    'If you have an existing SendOutCards login, use the same username and password to sign in to any of our stream websites.',
}

const phoneNumberCollisionCopy = {
  title: 'Phone Number Already Exists',
  message:
    'Because we use a single-sign-in process you only need one account and login for all of our streams. We ask for your phone number to ensure duplicate accounts are not being created. You can also use your phone number to log in instead of your username and password if you’d prefer.',
}

interface Props {
  onAfterSave?: () => void
  onToggleJoinLogin?: (name: LoginJoinForgot) => void
  isAffiliateJoining?: boolean
}

interface CreateAccountFormState {
  email: String
  username: String
  password: String
}

interface SponsorInfo {
  userId: string | undefined
  genealogyId: number | undefined
  slug: string | undefined
}

type AffiliateJoinSteps = 'createAccount' | 'affiliateAddress' | 'paymentMethod'

const CreateAccountForm: React.FC<Props> = props => {
  const { onAfterSave, onToggleJoinLogin, isAffiliateJoining } = props
  const { isMobile } = useSelector(state => state.window)

  const { account } = usePersistedUserData()
  const queryParams = useQueryParams()
  const vertical = useVertical()

  const [details, setDetails] = useState({
    email: account?.email ?? '',
    username: account?.username ?? '',
    password: '',
  })

  const [isCheckingUsername, setIsCheckingUsername] = useState(false)
  const [isCheckingPhoneNumber, setIsCheckingPhoneNumber] = useState(false)

  const [isPickingSponsor, setIsPickingSponsor] = useState(false)
  const [isDoingCCDetails, setIsDoingCCDetails] = useState(false)

  const [sponsorInfo, setSponsorInfo] = useState<SponsorInfo>()

  const sponsorInfoFromURL =
    !account && queryParams.sponsor ? queryParams.sponsor : undefined

  const isSponsorGeneaologyId =
    sponsorInfoFromURL && parseInt(sponsorInfoFromURL, 10)

  const [fetchSponsor, phoneNumberCountriesAll, marketingCopy] = useQueries(
    // search for a sponsor by their Geneaology Id, their Slug or their userId
    sponsorInfoFromURL && isSponsorGeneaologyId
      ? getSponsors({ search: sponsorInfoFromURL })
      : sponsorInfoFromURL
      ? getSponsorBySlug({ slug: sponsorInfoFromURL })
      : sponsorInfo?.userId && sponsorInfo?.userId !== '84109'
      ? getSponsor({ id: sponsorInfo.userId })
      : undefined,
    getPhoneNumberCountries(),
    getMarketingContent(),
  )

  const sponsorFragment = Array.isArray(fetchSponsor)
    ? fetchSponsor[0]
    : fetchSponsor

  const [affiliateStep, setAffiliateStep] = useState<AffiliateJoinSteps>(
    'createAccount',
  )
  const [createdAccount, setCreatedAccount] = useState<AccountFragment>()
  const [isWelcomeMessageOpen, setIsWelcomeMessageOpen] = useState(false)

  const [affiliateFormInfo, setAffiliateFormInfo] = useState<AffiliateFormInfo>(
    {
      user: {
        firstName: '',
        lastName: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
      },
      govId1: '',
      govId2: '',
      phoneNumber: '',
      accepted: false,
      purchased: false,
    },
  )

  // These are for GS affiliate join only, will need ALL phoneNumberCountries for all other applications
  const phoneNumberCountriesLimited: PhoneNumberCountryFragment[] = [
    { callingCode: 61, isoCode: 'AU', name: 'Australia' },
    { callingCode: 1, isoCode: 'CA', name: 'Canada' },
    { callingCode: 353, isoCode: 'IE', name: 'Ireland' },
    { callingCode: 64, isoCode: 'NZ', name: 'New Zealand' },
    { callingCode: 1, isoCode: 'US', name: 'United States' },
    { callingCode: 44, isoCode: 'GB', name: 'United Kingdom' },
  ]

  const phoneNumberCountries =
    vertical.id === 'greenerstill'
      ? phoneNumberCountriesLimited
      : phoneNumberCountriesAll

  const [selectedCountryIndex, setSelectedCountryIndex] = useState(
    phoneNumberCountries.findIndex(country => country.isoCode === 'US'),
  )

  const [phoneNumber, setPhoneNumber] = useState('')

  const selectedCountry = phoneNumberCountries[selectedCountryIndex]
  const selectedCountryCode = selectedCountry.isoCode
  const formattedNumber = getFormattedNumber(selectedCountryCode, phoneNumber)
  const validatedNumber = getValidatedNumber(selectedCountryCode, phoneNumber)

  const onSelectedSponsor = (id: string) => {
    setSponsorInfo({ userId: id, genealogyId: undefined, slug: undefined })
    setIsPickingSponsor(false)
  }

  const [isUserNameTaken, setIsUserNameTaken] = useState(false)
  const [isPhoneNumberTaken, setIsPhoneNumberTaken] = useState(false)
  const [isSaving, setIsSaving] = useState(0)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isUsernameTakenModalOpen, setIsUsernameTakenModalOpen] = useState(
    false,
  )
  const [
    isPhoneNumberCollisionModalOpen,
    setIsPhoneNumberCollisionModalOpen,
  ] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [userLoginLink, setUserLoginLink] = useState<string>()

  const mutations = useMutations()
  const actions = useActions()

  useEffect(() => {
    setSponsorInfo({
      userId: sponsorFragment?.id,
      genealogyId: sponsorFragment?.genealogyId,
      slug: sponsorFragment?.storefront?.slug
        ? sponsorFragment.storefront.slug
        : undefined,
    })
  }, [setSponsorInfo, sponsorFragment])

  useEffect(() => {
    setIsUserNameTaken(false)
  }, [details.username])

  const debouncedUsername = useDebounce(details.username, 500)
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let mutableCanceling = false
    const isUsernamePristine = account && debouncedUsername === account.username

    if (debouncedUsername && !isUsernamePristine) {
      const validate = async () => {
        setIsCheckingUsername(true)
        const {
          validateUsername: { valid: isValid },
        } = await mutations.validateUsername({
          username: debouncedUsername,
          doNotShowDefaultTransition: true,
        })
        setIsCheckingUsername(false)
        if (mutableCanceling) {
          return
        }
        setIsUserNameTaken(!isValid)
      }
      validate()
    }

    return () => {
      mutableCanceling = true
    }
  }, [debouncedUsername, account, mutations])

  useEffect(() => {
    setIsPhoneNumberTaken(false)
  }, [phoneNumber, selectedCountry])

  const debouncedPhoneNumber = useDebounce(phoneNumber, 500)
  const phoneNumberInput = createPhoneNumberInput(
    debouncedPhoneNumber,
    selectedCountry,
  )
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let mutableCanceling = false
    const isPhoneNumberPristine =
      account && phoneNumberInput.nationalNumber === account.phoneNumber

    if (phoneNumberInput.nationalNumber && !isPhoneNumberPristine) {
      const validate = async () => {
        setIsCheckingPhoneNumber(true)

        const {
          validatePhoneNumber: { isValid },
        } = await mutations.validatePhoneNumber({
          phoneNumber: phoneNumberInput,
          doNotShowDefaultTransition: true,
        })
        setIsCheckingPhoneNumber(false)
        if (mutableCanceling) {
          return
        }
        setIsPhoneNumberTaken(!isValid)
      }
      validate()
    }

    return () => {
      mutableCanceling = true
    }
  }, [account, mutations, selectedCountry, phoneNumberInput])

  const redirectUriBuilder = useRedirectUriBuilder()

  const isDisabled =
    isSaving > 0 ||
    !details.username ||
    !details.password ||
    !details.email ||
    isPhoneNumberTaken ||
    isUserNameTaken ||
    !validatedNumber ||
    isCheckingPhoneNumber ||
    (queryParams.payment && !affiliateFormInfo.stripeToken)

  const createAccountTitle =
    affiliateStep === 'createAccount'
      ? 'Become an Affiliate'
      : affiliateStep === 'affiliateAddress'
      ? 'Affiliate Information'
      : 'Affiliate Verification'

  const handleChange = (name: keyof CreateAccountFormState, value: string) => {
    const sanitizedValue =
      name === 'username' ? value.replace(/[^a-z0-9_\-@.+]|\s+/gim, '') : value
    setDetails({ ...details, [name]: sanitizedValue })
  }
  const hasAllRequiredFields = (info: AffiliateFormInfo): boolean => {
    return !(
      !details.username ||
      !details.password ||
      !details.email ||
      !phoneNumber ||
      !info.user.firstName ||
      !info.user.lastName ||
      !info.user.address1 ||
      !info.user.postalCode ||
      !info.user.city ||
      !info.user.state ||
      !info.user.country ||
      !info.govId1 ||
      !info.accepted ||
      !info.stripeToken
    )
  }

  const onSubmitAffiliateJoin = async (info: AffiliateFormInfo) => {
    if (isCheckingUsername || isCheckingPhoneNumber) return

    setAffiliateFormInfo(info)

    if (isAffiliateJoining) {
      switch (affiliateStep) {
        case 'createAccount':
          setAffiliateStep('affiliateAddress')
          return
        case 'affiliateAddress':
          setAffiliateStep('paymentMethod')
          return
        case 'paymentMethod':
          if (!hasAllRequiredFields(info)) {
            return
          }
          setIsSaving(x => x + 1)

          const accountInput: AccountInput = {
            ...details,
            sponsor: sponsorInfo?.userId,
            firstName: info.user.firstName,
            lastName: info.user.lastName,
            shippingAddress: { ...info.user },
            phoneNumber: `${selectedCountry.callingCode} ${phoneNumber}`,
            sponsorGenealogyId: sponsorInfo
              ? sponsorInfo.genealogyId?.toString()
              : '9070',
            stripeToken: info.stripeToken?.id,
          }

          try {
            const { govId1, govId2 } = info
            const {
              createDistributor: { account, error },
              createUserLoginLink: { link },
            } = await mutations.createAffiliate({
              account: {
                ...accountInput,
              },
              govId1,
              govId2,
              verticalId: vertical.graphqlVerticalID,
              queryParams,
            })
            if (account) {
              setUserLoginLink(link ?? undefined)
              setCreatedAccount(account)
              setIsWelcomeMessageOpen(true)
            } else {
              setErrorMessage(error ?? 'Failed to create affiliate account.')
            }
          } catch (error) {
            if (error.message === '0') {
              setErrorMessage(
                `The SSN value ${info.govId1} already exists in our system. Please go back and log in to your existing affiliate account. If you need further assistance, please contact support.`,
              )
            } else {
              setErrorMessage(
                error?.message ?? 'Failed to create affiliate account.',
              )
            }
          } finally {
            setIsSaving(x => x - 1)
          }
      }
    }
  }

  const onSubmit = async () => {
    if (isCheckingUsername || isCheckingPhoneNumber) return

    if (queryParams.payment && !affiliateFormInfo.stripeToken) {
      setErrorMessage('Payment method is required.')
      return
    }

    setIsSaving(x => x + 1)

    const userInfoUpdate: AccountInput = {
      ...details,
      sponsor: sponsorInfo?.userId,
      firstName: '',
      lastName: '',
      phoneNumber: `${selectedCountry.callingCode} ${phoneNumber}`,
      sponsorGenealogyId: sponsorInfo
        ? sponsorInfo.genealogyId?.toString()
        : '9070',
      stripeToken: affiliateFormInfo.stripeToken?.id,
    }

    try {
      const {
        createAccount: { account },
      } = await mutations.createAccount({
        account: userInfoUpdate,
        verticalId: vertical.graphqlVerticalID,
      })
      setCreatedAccount(account)
      setIsWelcomeMessageOpen(true)
    } finally {
      setIsSaving(x => x - 1)
    }
  }

  const onWelcomeMessageFinish = () => {
    if (!createdAccount) return
    const redirectUri = userLoginLink ?? redirectUriBuilder(createdAccount)
    actions.loginDone(Result(createdAccount))
    if (redirectUri) {
      window.location.href = redirectUri
    } else {
      if (onAfterSave) {
        onAfterSave()
      }
      setDetails(x => ({ ...x, password: '' }))
      actions.openAccount()
    }
  }

  return (
    <>
      {isWelcomeMessageOpen ? (
        <div>
          <Text type="subtitle">Welcome!</Text>
          <Spacer space="x2" />
          <Text type="body">
            By securely registering your unique username and password, you can
            safely browse all of our six income streams and our parent company,
            Greener Still. The single-user sign-on process is our commitment to
            a better way.
            <br />
            <br />
            Your information is protected through SSL connections to ensure your
            data is kept safe. Your sensitive information will never be shared
            or sold and is fully encrypted and secure at all times. Enjoy the
            Greener Still experience.
          </Text>
          <Spacer space="x6" />
          <Button
            fullWidth={true}
            onClick={onWelcomeMessageFinish}
            title="Finish"
          />
        </div>
      ) : isPickingSponsor ? (
        <FindSponsor
          onSelectedSponsor={onSelectedSponsor}
          isAffiliateJoining={isAffiliateJoining}
        />
      ) : isDoingCCDetails ? (
        <PaymentMethod
          shouldHideAffiliateFields={true}
          affiliateInfo={affiliateFormInfo}
          onFinishAccount={() => {}}
          onBack={info => {
            setAffiliateFormInfo(info)
            setIsDoingCCDetails(false)
          }}
        />
      ) : (
        <>
          {!account &&
            (!isAffiliateJoining ? (
              <div>
                <Text
                  type="title"
                  weight="semiBold"
                  outset={{ bottom: isMobile ? 'x1' : 'x2' }}
                >
                  <div
                    style={{
                      textAlign: isMobile ? 'center' : undefined,
                      display:
                        vertical.id === 'soc' && isMobile ? 'flex' : undefined,
                    }}
                  >
                    {vertical.id === 'soc' && isMobile && (
                      <Icon
                        icon="SOCLOGO"
                        color="blueViolet"
                        size={50}
                        style={{ margin: '0 5px 0 0' }}
                      />
                    )}
                    Create an Account
                  </div>
                </Text>
                <Text type="caption" alignment="center">
                  {marketingCopy.createAccountMessage.content}
                </Text>
                <Spacer space="x1" />
              </div>
            ) : (
              <div>
                <Text
                  type="title"
                  weight="semiBold"
                  outset={{ bottom: isMobile ? 'x1' : 'x2' }}
                >
                  <div
                    style={{
                      textAlign: isMobile ? 'center' : undefined,
                      display:
                        vertical.id === 'soc' && isMobile ? 'flex' : undefined,
                    }}
                  >
                    {vertical.id === 'soc' && isMobile && (
                      <Icon
                        icon="SOCLOGO"
                        color="blueViolet"
                        size={50}
                        style={{ margin: '0 5px 0 0' }}
                      />
                    )}
                    {createAccountTitle}
                  </div>
                </Text>
              </div>
            ))}
          {affiliateStep === 'createAccount' && (
            <form>
              <Flex width="100%" flexDirection="column">
                <Input
                  onChange={email => handleChange('email', email)}
                  type="email"
                  name="email"
                  value={details.email}
                  icon="envelope"
                  placeholder="Email"
                  focusColor="primaryBrand"
                  isFullWidth={true}
                />
                <Spacer space={'x1'} />
                <Input
                  onChange={username => handleChange('username', username)}
                  type="text"
                  name="username"
                  value={details.username}
                  icon="user"
                  placeholder="Username"
                  focusColor="primaryBrand"
                  message={
                    isUserNameTaken
                      ? {
                          type: 'danger',
                          content: 'This username is taken',
                          icon: 'information',
                          onClick: () => setIsUsernameTakenModalOpen(true),
                        }
                      : undefined
                  }
                  isFullWidth={true}
                  isLoading={isCheckingUsername}
                />
                <Text
                  type="footnote"
                  content={'Usernames are case sensitive'}
                  outset="x1"
                />
                <Spacer space={'x1'} />
                <Input
                  onChange={password => handleChange('password', password)}
                  type="password"
                  name="password"
                  value={details.password}
                  icon="lock"
                  placeholder="Password"
                  focusColor="primaryBrand"
                  isFullWidth={true}
                />
                <Spacer space={'x1'} />
                <PhoneInput
                  countries={phoneNumberCountries}
                  selectedCountryIndex={selectedCountryIndex}
                  onSelectedCountryIndexChange={setSelectedCountryIndex}
                  phoneNumber={formattedNumber}
                  onPhoneNumberChange={setPhoneNumber}
                  maxHeight="125px"
                  message={
                    !validatedNumber && debouncedPhoneNumber !== ''
                      ? {
                          type: 'danger',
                          content: 'Please enter a valid phone number.',
                        }
                      : isPhoneNumberTaken
                      ? {
                          type: 'danger',
                          content:
                            'This phone number exists on another account. Please log in with those account credentials.',
                          icon: 'information',
                          onClick: () =>
                            setIsPhoneNumberCollisionModalOpen(true),
                        }
                      : undefined
                  }
                />
                {isPhoneNumberTaken && <Spacer space={'x3'} />}
                <Spacer space={'x1'} />
                {queryParams.payment && (
                  <Card
                    width="100%"
                    borderRadius="medium"
                    inset="x2"
                    cursor="pointer"
                  >
                    <Flex
                      width="100%"
                      justifyContent="space-between"
                      alignItems="center"
                      onClick={() => setIsDoingCCDetails(true)}
                    >
                      <Flex alignItems="center">
                        <Span
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          inset={{ right: 'x1' }}
                        >
                          <div
                            style={{
                              backgroundImage: `linear-gradient(115deg, #c7c7c7, #878787)`,
                            }}
                          >
                            <CreditCardLogo
                              brand={creditCardBrand(
                                affiliateFormInfo.stripeToken?.card?.brand ??
                                  '',
                              )}
                            />
                          </div>
                        </Span>
                        <InfoBlock
                          size="small"
                          heading={
                            affiliateFormInfo.stripeToken?.id
                              ? `${
                                  affiliateFormInfo.stripeToken.card?.last4
                                    ? `XXXX XXXX XXXX ${affiliateFormInfo.stripeToken.card?.last4}`
                                    : 'Credit Card'
                                }`
                              : 'Payment Source'
                          }
                          caption={
                            affiliateFormInfo.stripeToken?.id
                              ? `${affiliateFormInfo.stripeToken.card?.brand}`
                              : 'Required'
                          }
                        />
                      </Flex>
                      <span
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignContent: 'center',
                        }}
                        onClick={e => {
                          e.stopPropagation()
                          console.log(true)
                        }}
                      >
                        <QDSIcon
                          name="information"
                          primaryColor="primaryHeadingText"
                          size="small"
                        />
                      </span>
                    </Flex>
                  </Card>
                )}
                <Spacer space={'x1'} />
                {sponsorFragment && sponsorInfo?.genealogyId !== 9070 ? (
                  <SelectableCard
                    isActive={true}
                    onClick={() => setIsPickingSponsor(true)}
                  >
                    <UserCard
                      profileImage={sponsorFragment.storefront?.mugshot?.url}
                      firstName={sponsorFragment.firstName}
                      lastName={sponsorFragment.lastName}
                      caption={formatLocation(
                        (sponsorFragment as SponsorFragment).shippingAddress
                          ?.city,
                        (sponsorFragment as SponsorFragment).shippingAddress
                          ?.state,
                      )}
                      size="small"
                      active={true}
                    />
                  </SelectableCard>
                ) : (
                  <Card
                    width="100%"
                    borderRadius="medium"
                    inset="x2"
                    cursor="pointer"
                  >
                    <Flex
                      width="100%"
                      justifyContent="space-between"
                      alignItems="center"
                      onClick={() => setIsPickingSponsor(true)}
                    >
                      <Flex alignItems="center">
                        <Span
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          inset={{ right: 'x1' }}
                        >
                          <QDSIcon
                            name="userSuccess"
                            primaryColor="default"
                            secondaryColor="background"
                            size="medium"
                          />
                        </Span>
                        <InfoBlock
                          size="small"
                          heading={
                            isAffiliateJoining
                              ? 'Who introduced you to our products?'
                              : 'Who showed you this product?'
                          }
                          caption="Optional"
                        />
                      </Flex>
                      <span
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignContent: 'center',
                        }}
                        onClick={e => {
                          e.stopPropagation()
                          setIsInfoModalOpen(true)
                        }}
                      >
                        <QDSIcon
                          name="information"
                          primaryColor="primaryHeadingText"
                          size="small"
                        />
                      </span>
                    </Flex>
                  </Card>
                )}
              </Flex>

              <Flex width="100%" flexDirection="column">
                <Spacer space={isMobile ? 'x1' : 'x4'} />
                <Div width="100%" id="create_my_account_btn">
                  <Button
                    fullWidth={true}
                    type={'primary'}
                    onClick={
                      isAffiliateJoining
                        ? () => onSubmitAffiliateJoin(affiliateFormInfo)
                        : () => onSubmit()
                    }
                    title={
                      isSaving
                        ? 'Creating account...'
                        : isAffiliateJoining
                        ? 'Continue'
                        : 'Create My Account'
                    }
                    disabled={isDisabled}
                  />
                </Div>
              </Flex>
              <Spacer space="x2" />
              <Div width="100%">
                <Separator
                  orientation="horizontal"
                  dashed={true}
                  caption="Or"
                />
              </Div>
              <Spacer space="x2" />
              <Flex width="100%" flexDirection="column">
                {vertical.id === 'soc' && (
                  <>
                    <Div width="100%">
                      <Button
                        hover={true}
                        fullWidth={true}
                        title="Send Your First Card Free"
                        onClick={() => {
                          actions.openPhoneNumberVerification()
                        }}
                        type="secondary"
                      />
                    </Div>
                    <Spacer space="x2" />
                  </>
                )}

                {onToggleJoinLogin && (
                  <>
                    {isAffiliateJoining && (
                      <div>
                        <Text type="caption" weight="thin">
                          {marketingCopy.affiliateLoginMessage.content}
                        </Text>
                        <Spacer space="x2" />
                      </div>
                    )}
                    <Button
                      hover={true}
                      type="secondary"
                      outlined={true}
                      onClick={() => onToggleJoinLogin?.('login')}
                      title="Log In to Your Existing Account"
                    />
                  </>
                )}
              </Flex>
              <ConfirmDialog
                zIndex={1}
                isOpen={isInfoModalOpen}
                title={sponsorSelectionCopy.title}
                description={sponsorSelectionCopy.message(vertical.name)}
                accept={{
                  title: 'Got it',
                  onClick: () => setIsInfoModalOpen(false),
                }}
                primaryAction={'accept'}
                onClose={() => setIsInfoModalOpen(false)}
              />
              <ConfirmDialog
                zIndex={1}
                isOpen={isUsernameTakenModalOpen}
                title={usernameTakenCopy.title}
                description={usernameTakenCopy.message}
                accept={{
                  title: 'Got it',
                  onClick: () => setIsUsernameTakenModalOpen(false),
                }}
                primaryAction={'accept'}
                onClose={() => setIsUsernameTakenModalOpen(false)}
              />
              <ConfirmDialog
                zIndex={1}
                isOpen={isPhoneNumberCollisionModalOpen}
                title={phoneNumberCollisionCopy.title}
                description={phoneNumberCollisionCopy.message}
                accept={{
                  title: 'Got it',
                  onClick: () => setIsPhoneNumberCollisionModalOpen(false),
                }}
                primaryAction={'accept'}
                onClose={() => setIsPhoneNumberCollisionModalOpen(false)}
              />
            </form>
          )}
          {affiliateStep === 'affiliateAddress' && (
            <AffiliateAddress
              affiliateInfo={affiliateFormInfo}
              onFinishAccount={affiliateInfo => {
                onSubmitAffiliateJoin(affiliateInfo)
              }}
              onBack={() => setAffiliateStep('createAccount')}
            />
          )}
          {affiliateStep === 'paymentMethod' && (
            <PaymentMethod
              affiliateInfo={affiliateFormInfo}
              onFinishAccount={affiliateInfo => {
                onSubmitAffiliateJoin(affiliateInfo)
              }}
              onBack={info => {
                setAffiliateFormInfo(info)
                setAffiliateStep('affiliateAddress')
              }}
            />
          )}
          <ConfirmDialog
            zIndex={1}
            isOpen={!!errorMessage}
            title="Failed to Join"
            description={errorMessage ?? ''}
            accept={{
              title: 'OK',
              onClick: () => {
                window.location.href =
                  'https://app.sendoutcards.com/dashboard/account?client=greenerstill'
              },
            }}
          />
        </>
      )}
    </>
  )
}

export default CreateAccountForm
